import React from 'react';
import classes from './WeeklyAppointments.module.css';
import { SalesOrderInfo } from '../../../store/slices/salesOrders';
import moment from 'moment/moment';
import { getTimeSlots } from '../../../utils/scheduleHelpers';
import { WeeklyViewEntry } from './WeeklyViewEntry';
import calendarWeekly from '../../../assets/icons/calendar-weekly.svg';

import { useAppSelector } from '../../../store/store';
import { IAccountInfo, selectAccountInfo, selectUserRole } from '../../../store/slices/auth';
import { DOCTOR_USER_ROLE } from '../../../constants/userRolesConstants';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  selectedSalesOrders: SalesOrderInfo[];
  selectedRange: string[];
  toggleDailyView: () => void;
  calendarType?: string;
  handleUpdateAppointment?: (
    salesOrder: SalesOrderInfo,
    start: Date,
    end: Date,
    callback: () => void
  ) => void;
  loadingUpdate?: boolean;
};

type DataToDisplay = { time: string; value: SalesOrderInfo[] };
const WeeklyAppointments: React.FC<Props> = ({
  selectedSalesOrders,
  selectedRange,
  toggleDailyView,
  calendarType,
  handleUpdateAppointment,
  loadingUpdate,
}) => {
  const weekDaysRef = React.useRef<HTMLInputElement>(null);
  const mainContainerRef = React.useRef<HTMLInputElement>(null);
  const hoursRef = React.useRef<HTMLInputElement>(null);
  const { translations } = useTranslation();
  const [hoveringMain, setHoveringMain] = React.useState(false);
  const [hoveringSides, setHoveringSides] = React.useState(false);
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const userRole: string = useAppSelector<string>(selectUserRole);
  const lang = localStorage.getItem('lang') || 'en';

  const groupSlots = (dateString: string, salesOrders: SalesOrderInfo[]) => {
    const dataToDisplay: DataToDisplay[] = [];
    const date = new Date(dateString);
    const timeSlots: Date[] = getTimeSlots(date, 0, 24, 1);
    timeSlots.forEach((tS) => {
      const _tS = moment(tS);
      let matchingSalesOrders: SalesOrderInfo[] = [];
      salesOrders.forEach((salesOrder) => {
        let _tSNext = moment(_tS).add({ hours: 1 });
        if (salesOrder.sales_order_appointment.start) {
          const startDate = moment(salesOrder.sales_order_appointment.start);
          if (startDate.isBetween(_tS, _tSNext, undefined, '[)')) {
            matchingSalesOrders.push(salesOrder);
          }
        }
      });
      dataToDisplay.push({
        time: `${_tS.format('HH')}:${_tS.format('mm')}`,
        value: matchingSalesOrders,
      });
    });
    return dataToDisplay;
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.textContent}>
          <div className={classes.headerTitle}>{translations('weekly.appointments')}</div>
          <div className={classes.headerSubTitle}>
            {`${moment(selectedRange[0]).format('DD.MM')} - ${moment(selectedRange[6]).format(
              'DD.MM'
            )}`}
          </div>
        </div>
        <div className={classes.headerRightContainer}>
          {accountInfo.serves_consultations && userRole !== DOCTOR_USER_ROLE && (
            <div className={classes.colorInfoContainer}>
              <div className={classes.colorIcon} style={{ backgroundColor: '#DBDFEA' }} />
              <div className={classes.colorText}>{translations('label.unassigned_doctor')}</div>
            </div>
          )}
          <div className={classes.colorInfoContainer}>
            <div className={classes.colorIcon} style={{ backgroundColor: '#FFFDE1' }} />
            <div className={classes.colorText}>{translations('schedule.diagnostics')}</div>
          </div>
          <div className={classes.colorInfoContainer}>
            <div className={classes.colorIcon} style={{ backgroundColor: '#E5F2FC' }} />
            <div className={classes.colorText}>{translations('schedule.consultations')}</div>
          </div>
          <img
            className={classes.toggleButton}
            src={calendarWeekly}
            alt={'calendar'}
            onClick={toggleDailyView}
          />
        </div>
      </div>
      <div className={classes.main}>
        <div
          className={classes.hoursContainer}
          ref={hoursRef}
          onMouseEnter={() => setHoveringSides(true)}
          onMouseLeave={() => setHoveringSides(false)}
          onScroll={() => {
            if (!hoveringMain && mainContainerRef.current && hoursRef.current) {
              mainContainerRef.current.scrollTop = hoursRef.current.scrollTop;
            }
          }}
        >
          {getTimeSlots(new Date(), 0, 24, 1).map((ts) => (
            <div key={ts.toString()} className={classes.hours}>
              {moment(ts).format('HH:mm')}
            </div>
          ))}
        </div>
        <div className={classes.weeklyAppointments}>
          <div
            ref={weekDaysRef}
            className={classes.weekDaysContainer}
            onMouseEnter={() => setHoveringSides(true)}
            onMouseLeave={() => setHoveringSides(false)}
            onScroll={() => {
              if (!hoveringMain && weekDaysRef.current && mainContainerRef.current) {
                (mainContainerRef.current as any).scrollLeft = (
                  weekDaysRef.current as any
                ).scrollLeft;
              }
            }}
          >
            {selectedRange.map((ds) => (
              <div className={classes.weekDay}>{moment(ds).locale(lang).format('dddd')}</div>
            ))}
          </div>
          <div
            ref={mainContainerRef}
            className={classes.appointmentsContainer}
            onMouseEnter={() => setHoveringMain(true)}
            onMouseLeave={() => setHoveringMain(false)}
            onScroll={() => {
              if (
                !hoveringSides &&
                mainContainerRef.current &&
                weekDaysRef.current &&
                hoursRef.current
              ) {
                weekDaysRef.current.scrollLeft = mainContainerRef.current.scrollLeft;
                hoursRef.current.scrollTop = mainContainerRef.current.scrollTop;
              }
            }}
          >
            {selectedRange.map((ds, index) => (
              <div key={index} className={classes.dayContainer}>
                {groupSlots(ds, selectedSalesOrders).map((GS, index) => (
                  <div key={`${GS.time}_${index}`} className={classes.timeSlot}>
                    {GS.value.length > 0 ? (
                      <WeeklyViewEntry
                        salesOrders={GS.value}
                        calendarType={calendarType}
                        handleUpdateAppointment={(salesOrder, start, end, callback) => {
                          handleUpdateAppointment &&
                            handleUpdateAppointment(salesOrder, start, end, callback);
                        }}
                        loadingUpdate={loadingUpdate}
                      />
                    ) : (
                      <div style={{ height: 160, width: 160 }} />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export { WeeklyAppointments };
